import React, { useState } from 'react';
import { Modal, Button, message, Upload, Avatar } from 'antd';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RcFile, UploadProps } from 'antd/es/upload';
import './PhotoUploadModal.css';

interface PhotoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoUpload: (file: File) => Promise<void>;
  currentPhotoUrl?: string;
  title: string;
  isLoading?: boolean;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  isOpen,
  onClose,
  onPhotoUpload,
  currentPhotoUrl,
  title,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setIsDragging(false);
    onClose();
  };

  const validateFile = (file: File): boolean => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Please upload an image file');
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      message.error('File size must be less than 5MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };


  const handleUpload = async () => {
    if (!selectedFile) {
      message.error('Please select a file first');
      return;
    }

    try {
      await onPhotoUpload(selectedFile);
      message.success(t('#Teams.photo_upload_success'));
      handleClose();
    } catch (error: any) {
      message.error(error.message || t('#Teams.photo_upload_error'));
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file: RcFile) => {
      handleFileSelect(file);
      return false; // Prevent auto upload
    },
    accept: 'image/*',
    multiple: false,
    showUploadList: false,
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      className="photo-upload-modal"
      width={480}
      closable={false}
      maskClosable={!isLoading}
    >
      <div className="photo-upload-content">
        {/* Header */}
        <div className="photo-upload-header">
          <h2 className="photo-upload-title">{title}</h2>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={handleClose}
            className="photo-upload-close"
            disabled={isLoading}
          />
        </div>

        {/* Current Photo */}
        {currentPhotoUrl && !previewUrl && (
          <div className="current-photo-section">
            <h4>Current Photo:</h4>
            <Avatar
              size={120}
              src={currentPhotoUrl}
              className="current-photo"
            />
          </div>
        )}

        {/* Preview Section */}
        {previewUrl && (
          <div className="preview-section">
            <h4>New Photo Preview:</h4>
            <Avatar
              size={120}
              src={previewUrl}
              className="preview-photo"
            />
          </div>
        )}

        {/* Drag & Drop Area */}
        <Upload {...uploadProps}>
          <div
            className={`drag-drop-area ${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="drag-drop-content">
              <UploadOutlined className="upload-icon" />
              <p className="drag-drop-text">
                {t('#Teams.drag_drop_photo')}
              </p>
              <p className="file-info">
                Maximum file size: 5MB
              </p>
            </div>
          </div>
        </Upload>

        {/* Upload Button with Ant Design Upload */}
        <div className="upload-button-section">
          <Upload {...uploadProps}>
            <Button
              icon={<UploadOutlined />}
              disabled={isLoading}
              className="select-file-button"
              type="default"
              size="large"
            >
              Select Photo
            </Button>
          </Upload>
        </div>

        {/* Action Buttons */}
        <div className="photo-upload-actions">
          <Button
            onClick={handleClose}
            disabled={isLoading}
            className="cancel-button"
          >
            {t('#Common.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleUpload}
            loading={isLoading}
            disabled={!selectedFile}
            className="upload-button"
          >
            {t('#Teams.upload_photo')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PhotoUploadModal;